
import React, { useState, useEffect } from 'react';
import { INSTRUCTION_MANUAL_CONTENT } from '../../services/instructionManualContent';
import type { ManualSection } from '@types';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import { ICONS } from './constants';
import { helpMarkdownClassName } from '../common/helpStyles';

const md = createSafeMarkdown({ html: true });

interface InstructionManualProps {
    onBack: () => void;
}

const InstructionManual: React.FC<InstructionManualProps> = ({ onBack }) => {
    const [selectedSection, setSelectedSection] = useState<ManualSection>(INSTRUCTION_MANUAL_CONTENT.sections[0]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Block Ctrl+P (Print), Ctrl+S (Save), Ctrl+C/X (Copy/Cut), Ctrl+U (Source), F12 (Dev Tools)
            if (
                (e.ctrlKey && (e.key === 'p' || e.key === 's' || e.key === 'c' || e.key === 'x' || e.key === 'u')) ||
                e.key === 'F12'
            ) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const filteredSections = INSTRUCTION_MANUAL_CONTENT.sections.filter(section =>
        section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        section.content.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const renderSidebar = () => {
        return (
            <div className="w-1/4 bg-slate-100 p-4 rounded-lg overflow-y-auto border-r relative z-10">
                <h2 className="text-xl font-bold mb-4 text-slate-800">Table of Contents</h2>
                <input
                    type="text"
                    name="instruction-manual-search"
                    id="instruction-manual-search"
                    placeholder="Search manual..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md mb-4 sticky top-0 bg-white"
                />
                <ul>
                    {filteredSections.map((section: ManualSection) => (
                        <li key={section.title} className="mb-2">
                            <button
                                onClick={() => setSelectedSection(section)}
                                className={`w-full text-left font-semibold p-2 rounded-md text-sm ${selectedSection.title === section.title ? 'bg-blue-500 text-white' : 'hover:bg-slate-200'}`}
                            >
                                {section.title}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderContent = () => {
        const renderedHtml = md.render(selectedSection.content);
        return (
            <div className="w-3/4 p-8 overflow-y-auto relative z-10">
                <button 
                    onClick={onBack} 
                    className="absolute top-6 right-8 flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
                >
                    {ICONS.backArrow && React.cloneElement(ICONS.backArrow as any, { className: 'h-5 w-5' })}
                    <span>Back</span>
                </button>

                <h1 className="text-4xl font-bold mb-4 text-slate-900">{INSTRUCTION_MANUAL_CONTENT.title}</h1>
                <p className="text-slate-600 mb-8 italic">{INSTRUCTION_MANUAL_CONTENT.introduction}</p>
                <div className="prose prose-slate max-w-none">
                    <h2 className="text-2xl font-bold mb-4 text-slate-800">{selectedSection.title}</h2>
                    <div
                        className={helpMarkdownClassName}
                        dangerouslySetInnerHTML={{ __html: renderedHtml }}
                    />
                </div>
            </div>
        );
    };

    return (
        <div 
            className="flex h-[calc(100vh-120px)] bg-white rounded-lg shadow-md animate-fade-in relative overflow-hidden select-none print:hidden"
            onContextMenu={(e) => e.preventDefault()}
        >
            <style>{`
                @media print {
                  body * { visibility: hidden !important; }
                  .no-print { display: none !important; }
                }
                .manual-watermark {
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  pointer-events: none;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  z-index: 5;
                  opacity: 0.05;
                  overflow: hidden;
                  user-select: none;
                }
                .manual-watermark-text {
                  transform: rotate(-30deg);
                  font-size: 6rem;
                  font-weight: bold;
                  white-space: nowrap;
                  color: #64748b;
                }
            `}</style>

            {/* Watermark Overlay */}
            <div className="manual-watermark" aria-hidden="true">
                <div className="manual-watermark-text">
                    PROPERTY OF INTELOPTICS • PROPRIETARY DATA
                </div>
            </div>

            {renderSidebar()}
            {renderContent()}
        </div>
    );
};

export default InstructionManual;
